/**
 * Service recrutement
 * Branché sur le même $api que le service principal (token + baseURL)
 */
export default ($http, $config) => {
  const $api = $http.create({
    baseURL: $config.serverURL,
    headers: { "Content-Type": "application/json" },
  });

  // Intercepteur auth — même pattern que ton service existant
  $api.interceptors.request.use(
    (config) => {
      const authToken = sessionStorage.getItem("AUTH_TOKEN_KEY");
      if (authToken) {
        config.headers["Authorization"] = `Bearer ${authToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ─── Offres ─────────────────────────────────────────────────────────────────

  /** GET /api/offers — liste des offres actives (landing page) */
  const getActiveOffers = () => {
    return $api.get(`/${$config.apiVersion}offers`);
  };

  /** GET /api/offers/:id — détail d'une offre */
  const getOffer = (id) => {
    return $api.get(`/${$config.apiVersion}offers/${id}`);
  };

  // ─── OTP ────────────────────────────────────────────────────────────────────

  /** POST /api/otp/send — envoie un code OTP par mail */
  const sendOtp = (data) => {
    return $api.post(`/${$config.apiVersion}otp/send`, data);
    // data: { email, offerId }
  };

  /**
   * POST /api/otp/verify — vérifie le code OTP
   * Réponse : { verified, message, sessionToken, sessionExpiresAt }
   * sessionToken (valable 7 jours sur cet appareil) est à joindre aux
   * documents, au brouillon et à la candidature.
   */
  const verifyOtp = (data) => {
    return $api.post(`/${$config.apiVersion}otp/verify`, data);
    // data: { email, offerId, code }
  };

  /**
   * NOUVEAU — POST /api/otp/logout — « Ce n'est pas vous ? »
   * Ferme la session de cet appareil (brouillon et documents restent sur le serveur).
   */
  const logoutCandidate = (data) => {
    return $api.post(`/${$config.apiVersion}otp/logout`, data, {
      timeout: 15000,
    });
    // data: { email, offerId, sessionToken }
  };

  /** NOUVEAU — GET /api/otp/rate-limit — nombre d'envois de code restants */
  const getOtpRateLimit = (params) => {
    return $api.get(`/${$config.apiVersion}otp/rate-limit`, {
      params, // { email, offerId }
      timeout: 15000,
    });
  };

  // ─── Candidature ────────────────────────────────────────────────────────────

  /**
   * POST /api/applications — soumet une candidature
   * Les documents sont déjà téléversés (uploadApplicationFile) : le corps ne
   * contient que les champs texte, sessionToken et stagedFileIds.
   * `options` : options axios supplémentaires (ex. timeout).
   */
  const submitApplication = (formData, options = {}) => {
    return $api.post(`/${$config.apiVersion}applications`, formData, {
      timeout: 120000,
      ...options,
      headers: {
        "Content-Type": "multipart/form-data",
        ...(options.headers || {}),
      },
    });
  };

  // ─── NOUVEAU : documents de candidature, téléversés dès leur sélection ─────
  // (retrouvés si le candidat ferme la page ou change d'appareil)

  /**
   * POST /api/applications/files (multipart)
   * formData : email, offerId, sessionToken, fieldKey, file
   * onProgress(pourcentage) : 0 → 99 pendant l'envoi
   */
  const uploadApplicationFile = (formData, onProgress) => {
    return $api.post(`/${$config.apiVersion}applications/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000, // réseau mobile lent
      onUploadProgress: (e) => {
        if (onProgress && e && e.total) {
          onProgress(Math.min(99, Math.round((e.loaded * 100) / e.total)));
        }
      },
    });
  };

  /** POST /api/applications/files/list — documents déjà téléversés */
  const listApplicationFiles = (data) => {
    return $api.post(`/${$config.apiVersion}applications/files/list`, data, {
      timeout: 30000,
    });
    // data: { email, offerId, sessionToken }
  };

  /** POST /api/applications/files/:id/delete — retire un document */
  const deleteApplicationFile = (id, data) => {
    return $api.post(
      `/${$config.apiVersion}applications/files/${id}/delete`,
      data,
      { timeout: 30000 }
    );
    // data: { email, offerId, sessionToken }
  };

  // ─── NOUVEAU : brouillon (réponses texte) conservé sur le serveur ──────────
  // (retrouvé sur n'importe quel appareil après vérification de l'e-mail)

  /** POST /api/applications/draft/load → { data, updatedAt } (data = null si aucun) */
  const loadApplicationDraft = (data) => {
    return $api.post(`/${$config.apiVersion}applications/draft/load`, data, {
      timeout: 30000,
    });
    // data: { email, offerId, sessionToken }
  };

  /** POST /api/applications/draft/save → { updatedAt } */
  const saveApplicationDraft = (data) => {
    return $api.post(`/${$config.apiVersion}applications/draft/save`, data, {
      timeout: 30000,
    });
    // data: { email, offerId, sessionToken, data: { firstName, …, extraFields, otherFields, currentStep } }
  };

  const getLandingList = (params) => {
    return $api.get(`/${$config.apiVersion}news-post/list`, {
      params,
    });
  };

  // ─── Gift Card Activations ──────────────────────────────────────────────────

  /** POST /api/gift-card-activations — active une carte cadeau */
  const submitGiftCardActivation = (data) =>
    $api.post(`/${$config.apiVersion}gift-card-activations`, data);

  /** GET /api/gift-card-activations/admin/all — liste (admin) */
  const getGiftCardActivations = (eventCode) =>
    $api.get(`/${$config.apiVersion}gift-card-activations/admin/all`, {
      params: eventCode ? { eventCode } : {},
    });

  /** GET /api/gift-card-activations/admin/stats — stats (admin) */
  const getGiftCardStats = (eventCode) =>
    $api.get(`/${$config.apiVersion}gift-card-activations/admin/stats`, {
      params: eventCode ? { eventCode } : {},
    });

  /** POST /api/domex-contracts — soumet une souscription (multipart) */
  const submitDomexContract = (formData) => {
    return $api.post(`/${$config.apiVersion}domex-contracts`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000, // 60s pour gérer les uploads CNI (jusqu'à 5 Mo × 2)
    });
  };

  const submitSanteSubscription = (formData) => {
    return $api.post(`/${$config.apiVersion}sante-subscriptions`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000, // photos + actes pour N personnes : marge large
    });
  };

  return {
    getActiveOffers,
    getOffer,
    sendOtp,
    verifyOtp,
    getOtpRateLimit,
    logoutCandidate,
    submitApplication,
    uploadApplicationFile,
    listApplicationFiles,
    deleteApplicationFile,
    loadApplicationDraft,
    saveApplicationDraft,
    getLandingList,
    submitGiftCardActivation,
    getGiftCardActivations,
    getGiftCardStats,
    submitDomexContract,
    submitSanteSubscription,
  };
};
